function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import _ from 'lodash';
import cx from 'clsx';
import PropTypes from 'prop-types';
import * as React from 'react';
import { childrenUtils, createShorthandFactory, customPropTypes, getComponentType, getUnhandledProps, htmlImageProps, partitionHTMLProps, SUI, getKeyOnly, getKeyOrValueAndKey, getValueAndKey, getVerticalAlignProp } from '../lib';
import Dimmer from '../elements/Dimmer/Dimmer';
import Label from '../Label/Label';
import ImageGroup from './ImageGroup';

/**
 * An image is a graphic representation of something.
 * @see Icon
 */
var Image = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var avatar = props.avatar,
    bordered = props.bordered,
    centered = props.centered,
    children = props.children,
    circular = props.circular,
    className = props.className,
    content = props.content,
    dimmer = props.dimmer,
    disabled = props.disabled,
    floated = props.floated,
    fluid = props.fluid,
    hidden = props.hidden,
    href = props.href,
    inline = props.inline,
    label = props.label,
    rounded = props.rounded,
    size = props.size,
    spaced = props.spaced,
    verticalAlign = props.verticalAlign,
    wrapped = props.wrapped,
    _props$ui = props.ui,
    ui = _props$ui === void 0 ? true : _props$ui;
  var classes = cx(getKeyOnly(ui, 'ui'), size, getKeyOnly(avatar, 'avatar'), getKeyOnly(bordered, 'bordered'), getKeyOnly(circular, 'circular'), getKeyOnly(centered, 'centered'), getKeyOnly(disabled, 'disabled'), getKeyOnly(fluid, 'fluid'), getKeyOnly(hidden, 'hidden'), getKeyOnly(inline, 'inline'), getKeyOnly(rounded, 'rounded'), getKeyOrValueAndKey(spaced, 'spaced'), getValueAndKey(floated, 'floated'), getVerticalAlignProp(verticalAlign, 'aligned'), 'image', className);
  var rest = getUnhandledProps(Image, props);
  var _partitionHTMLProps = partitionHTMLProps(rest, {
      htmlProps: htmlImageProps
    }),
    _partitionHTMLProps2 = _slicedToArray(_partitionHTMLProps, 2),
    imgTagProps = _partitionHTMLProps2[0],
    rootProps = _partitionHTMLProps2[1];
  var ElementType = getComponentType(props, {
    defaultAs: 'img',
    getDefault: function getDefault() {
      if (!_.isNil(dimmer) || !_.isNil(label) || !_.isNil(wrapped) || !childrenUtils.isNil(children)) {
        return 'div';
      }
    }
  });
  if (!childrenUtils.isNil(children)) {
    return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
      className: classes,
      ref: ref
    }), children);
  }
  if (!childrenUtils.isNil(content)) {
    return /*#__PURE__*/React.createElement(ElementType, _extends({}, rest, {
      className: classes,
      ref: ref
    }), content);
  }
  if (ElementType === 'img') {
    return /*#__PURE__*/React.createElement(ElementType, _extends({}, rootProps, imgTagProps, {
      className: classes,
      ref: ref
    }));
  }
  return /*#__PURE__*/React.createElement(ElementType, _extends({}, rootProps, {
    className: classes,
    href: href
  }), Dimmer.create(dimmer, {
    autoGenerateKey: false
  }), Label.create(label, {
    autoGenerateKey: false
  }), /*#__PURE__*/React.createElement("img", _extends({}, imgTagProps, {
    ref: ref
  })));
});
Image.Group = ImageGroup;
Image.displayName = 'Image';
Image.propTypes = {
  /** An element type to render as (string or function). */
  as: PropTypes.elementType,
  /** An image may be formatted to appear inline with text as an avatar. */
  avatar: PropTypes.bool,
  /** An image may include a border to emphasize the edges of white or transparent content. */
  bordered: PropTypes.bool,
  /** An image can appear centered in a content block. */
  centered: PropTypes.bool,
  /** Primary content. */
  children: PropTypes.node,
  /** An image may appear circular. */
  circular: PropTypes.bool,
  /** Additional classes. */
  className: PropTypes.string,
  /** Shorthand for primary content. */
  content: customPropTypes.contentShorthand,
  /** An image can show that it is disabled and cannot be selected. */
  disabled: PropTypes.bool,
  /** Shorthand for Dimmer. */
  dimmer: customPropTypes.itemShorthand,
  /** An image can sit to the left or right of other content. */
  floated: PropTypes.oneOf(SUI.FLOATS),
  /** An image can take up the width of its container. */
  fluid: customPropTypes.every([PropTypes.bool, customPropTypes.disallow(['size'])]),
  /** An image can be hidden. */
  hidden: PropTypes.bool,
  /** Renders the Image as an <a> tag with this href. */
  href: PropTypes.string,
  /** An image may appear inline. */
  inline: PropTypes.bool,
  /** Shorthand for Label. */
  label: customPropTypes.itemShorthand,
  /** An image may appear rounded. */
  rounded: PropTypes.bool,
  /** An image may appear at different sizes. */
  size: PropTypes.oneOf(SUI.SIZES),
  /** An image can specify that it needs an additional spacing to separate it from nearby content. */
  spaced: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['left', 'right'])]),
  /** Whether or not to add the ui className. */
  ui: PropTypes.bool,
  /** An image can specify its vertical alignment. */
  verticalAlign: PropTypes.oneOf(SUI.VERTICAL_ALIGNMENTS),
  /** An image can render wrapped in a `div.ui.image` as alternative HTML markup. */
  wrapped: PropTypes.bool
};
Image.create = createShorthandFactory(Image, function (value) {
  return {
    src: value
  };
});
export default Image;
//# sourceMappingURL=Image.js.map